import { FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Card from '@/components/ui/Card';
import { sponsorsEnabled } from '@/lib/feature-flags';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

export default function SponsorSignup() {
  if (!sponsorsEnabled()) {
    return <Navigate to="/" replace />;
  }

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (!email.trim()) {
      setError('Please add an email so we can send your secure checkout link.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${CORE_API}/api/v1/sponsors/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), display_name: displayName.trim() || undefined }),
      });

      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || 'Unable to save your sponsor request.');
      }

      setMessage(
        'Thanks! We’ll email you a secure $800 checkout link along with onboarding details within one business day.'
      );
      setDisplayName('');
      setEmail('');
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unable to send email.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <header className="space-y-2 text-center">
        <span className="text-xs uppercase tracking-wide text-cocoa/60">Sponsor Program</span>
        <h1 className="text-3xl font-semibold text-cocoa">Create your Sponsor Profile</h1>
        <p className="text-sm text-cocoa/70">
          We’ll send you the $800 checkout link, onboarding checklist, and guidance for claiming or updating your profile.
        </p>
      </header>

      <Card className="space-y-4 p-6">
        <form className="space-y-4" onSubmit={handleSubmit}>
          <label className="block text-sm">
            <span className="font-medium text-cocoa">Preferred display name</span>
            <input
              className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Jordan Smith"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-cocoa">Email</span>
            <input
              type="email"
              required
              className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
            />
          </label>

          <button className="btn w-full disabled:opacity-70" disabled={submitting} type="submit">
            {submitting ? 'Sending…' : 'Email me the $800 link'}
          </button>
        </form>

        {message ? <p className="text-sm text-mint">{message}</p> : null}
        {error ? <p className="text-sm text-brick">{error}</p> : null}
      </Card>

      <section className="space-y-2 rounded-3xl bg-white/70 p-5 text-sm text-cocoa/75">
        <h2 className="text-base font-semibold text-cocoa">Before you pay</h2>
        <ul className="list-disc space-y-1 pl-5">
          <li>$800 mints one Sponsor Slot. Additional slots require additional $800 payments.</li>
          <li>Slots are matched by Project Tower draws—there’s no browsing or manual selection.</li>
          <li>You can request a private checkout link for a specific classroom using their share code.</li>
        </ul>
      </section>
    </div>
  );
}
